import { useEffect, useState } from "react";
import type { SaleState } from "#/Reducers/SellScreen/SellScreenSaleReducerTypes.ts";
import type { IntegrationInProgress } from "#/Routes/SellScreen/SellScreenTypes.ts";
import type { AdditionalFields } from "../../Domain/AdditionalInformation";
import { parseString } from "../../Generic/Strings";
import type { ScalesSettings } from "../Hardware/Scales";
import type { AddOnName, SubscriptionName } from "../Subscription/Subscription";
import { defaultOptions as generatedDefaults, type Options as GeneratedOptions } from "./Options.gen";

export interface Options extends GeneratedOptions {
    // Extend the options here with local only options
    training_mode_enabled: boolean;
    loggedout: boolean;
    current_sale: SaleState | null;
    outlet: string | null;
    register: string | null;
    global_mode: boolean;
    register_kicked: string | null;
    live_profit: boolean;
    utc_offset: number;
    _instance_id: false | string;
    __shopfront_version: string;
    outlet_name: string;
    register_name: string;
    previous_register: string;
    register_open_time: string;
    [key: `close_register_${string}`]: number | null | undefined;
    [key: `client_${string}_download`]: boolean | null | undefined;
    [key: `client_${string}_redirect`]: boolean | null | undefined;
    outlet_loyalty: boolean;
    outlet_loyalty_name: string;
    outlet_loyalty_rate: number;
    outlet_redemption_rate: number;
    device_users: Record<string, string>;
    accounts_flow_enabled: boolean;
    _customer_display_previous: string;
    _customer_display_open_status: boolean;
    _sell_screen_current_display: string;
    _latest_database_version: "unknown" | number;
    _database_refresh: "force" | "unknown" | number;
    _force_close: boolean;
    lastsync: string | null;
    industry: "liquor" | "retail";
    "kitchen:bump:viewMode": 0 | 1;
    "kitchen:bump:screen": null | string;
    current_login_token: string;
    last_login_time: string | null;
    default_tax_rate_id: null | string;
    "__flag:product-edit-form:stage:2": boolean;
    register_closure_default_note: string;
    experiment_reporting_source: boolean;
    __integration_payment_in_progress: null | IntegrationInProgress;
    current_sale_keys_position: "left" | "right" | "default";
    additional: AdditionalFields;
    __scales: null | ScalesSettings;
    subscription_kitchen_screens: number;
    experiment_everyday_ticket_export: boolean;
    __serial_ports: Array<string>;
    subscription_name: SubscriptionName;
    subscription_addons: Array<AddOnName>;
    experiment_force_worker_skip_cache: null | boolean;
    customer_display_connection_id: null | string;
    force_sync_flag: string;
}

const defaultOptions: Options = {
    ...generatedDefaults,
    training_mode_enabled             : false,
    loggedout                         : false,
    current_sale                      : null,
    outlet                            : null,
    register                          : null,
    global_mode                       : false,
    register_kicked                   : null,
    live_profit                       : false,
    utc_offset                        : 0,
    _instance_id                      : false,
    __shopfront_version               : __VERSION__.commit,
    outlet_name                       : "",
    register_name                     : "",
    previous_register                 : "",
    register_open_time                : "",
    outlet_loyalty                    : false,
    outlet_loyalty_name               : "Loyalty",
    outlet_loyalty_rate               : 0,
    outlet_redemption_rate            : 0,
    device_users                      : {},
    accounts_flow_enabled             : false,
    _customer_display_previous        : "",
    _sell_screen_current_display      : "",
    _latest_database_version          : "unknown",
    _database_refresh                 : "unknown",
    _force_close                      : false,
    _customer_display_open_status     : false,
    lastsync                          : null,
    industry                          : "retail",
    "kitchen:bump:viewMode"           : 0,
    "kitchen:bump:screen"             : null,
    current_login_token               : "",
    last_login_time                   : null,
    default_tax_rate_id               : null,
    "__flag:product-edit-form:stage:2": true,
    register_closure_default_note     : "",
    experiment_reporting_source       : false,
    __integration_payment_in_progress : null,
    current_sale_keys_position        : "default",
    additional                        : [],
    subscription_kitchen_screens      : 0,
    experiment_everyday_ticket_export : false,
    __scales                          : null,
    __serial_ports                    : [],
    subscription_name                 : "premium",
    subscription_addons               : [],
    experiment_force_worker_skip_cache: null,
    customer_display_connection_id    : null,
    force_sync_flag                   : "",
};

export type OptionValue = string | boolean | number | Record<string, unknown> | undefined | null | Array<unknown>;

declare global {
    interface WindowEventMap {
        currentStorageUpdate: CustomEvent<{
            key: keyof Options;
            oldValue: OptionValue;
            newValue: OptionValue;
            url: string;
            storageArea: Storage;
        }>;
    }
}

const optionCache: Partial<Options> = {};
const tempOptionCache: Record<string, OptionValue> = {};

type ToPrimitive<T> =
    T extends string ?
        string :
        T extends number ?
            number :
            T extends boolean ?
                boolean :
                T;

/**
 * Get an option from the settings
 * @param option
 * @returns {OptionValue}
 */
export function getOption<TKey extends keyof Options>(option: TKey): Options[TKey] {
    if(typeof localStorage === "undefined") {
        return defaultOptions[option];
    }

    // Check if the option exists in the cache
    if(typeof optionCache[option] !== "undefined" && process.env.NODE_ENV !== "test") {
        return optionCache[option];
    }

    let value: string | null | Options[TKey] = localStorage.getItem(option);

    if(typeof value === "undefined" || value === null) {
        // Return the default value and add it to the cache
        optionCache[option] = defaultOptions[option];

        return defaultOptions[option];
    }

    value = parseString(value) as Options[TKey];

    // Add the retrieved option to the cache
    optionCache[option] = value;

    return value;
}

/**
 * Retrieve a temporary option from the session storage
 * @param option
 * @param defaultValue
 */
export function getTempOption<T extends OptionValue>(option: string, defaultValue: T): ToPrimitive<T> {
    if(typeof sessionStorage === "undefined") {
        return defaultValue as ToPrimitive<T>;
    }

    if(typeof tempOptionCache[option] !== "undefined" && process.env.NODE_ENV !== "test") {
        return tempOptionCache[option] as ToPrimitive<T>;
    }

    let value: OptionValue = sessionStorage.getItem(option);

    if(typeof value === "undefined" || value === null) {
        // Return the default value
        return defaultValue as ToPrimitive<T>;
    }

    value = parseString(value);

    // Add the retrieved option to the cache
    tempOptionCache[option] = value;

    return value as ToPrimitive<T>;
}

/**
 * Set an option in the local database
 * @param option
 * @param value
 * @param fireCurrentStorageEventLocally
 * @returns {OptionValue}
 */
export function setOption<TKey extends keyof Options>(
    option: TKey,
    value: Options[TKey],
    fireCurrentStorageEventLocally = true
): Options[TKey] {
    if(typeof value === "undefined") {
        throw new Error("Cannot set option (" + option + ") to undefined");
    }

    const originalValue = value;
    let valueToSet: string | Options[TKey] = value;

    if((typeof value === "object" && value !== null) || Array.isArray(value)) {
        valueToSet = JSON.stringify(value);
    }

    if(typeof localStorage === "undefined") {
        // Testing
        return value;
    }

    if(value === null) {
        valueToSet = "null";
    }

    // Store the value
    const oldValue = localStorage.getItem(option);

    localStorage.setItem(option, (valueToSet as string).toString());

    // Manually fire the event (for local subscribers)
    if(typeof window !== "undefined" && fireCurrentStorageEventLocally) {
        if(oldValue !== valueToSet) {
            const currentStorageEvent = new CustomEvent("currentStorageUpdate", {
                detail: {
                    key        : option,
                    oldValue   : oldValue,
                    newValue   : valueToSet,
                    url        : window.location.href,
                    storageArea: localStorage,
                },
            });

            window.dispatchEvent(currentStorageEvent);
        }
    }

    // Ensure the option cache is up to date
    if(typeof originalValue === "string") {
        optionCache[option] = parseString(originalValue) as Options[TKey];
    } else {
        optionCache[option] = originalValue;
    }

    // Return the value
    return value;
}

/**
 * Set a temporary option in the session storage
 * @param option
 * @param value
 * @returns {OptionValue}
 */
export function setTempOption(
    option: string,
    value: OptionValue
): OptionValue {
    if(typeof value === "undefined") {
        throw new Error("Cannot set option (" + option + ") to undefined");
    }

    const originalValue = value;

    if((typeof value === "object" && value !== null) || Array.isArray(value)) {
        value = JSON.stringify(value);
    }

    if(typeof sessionStorage === "undefined") {
        // Testing
        return value;
    }

    if(value === null) {
        value = "null";
    }

    // Store the value
    sessionStorage.setItem(option, value.toString());

    // Ensure the option cache is up to date
    if(typeof originalValue === "string") {
        tempOptionCache[option] = parseString(originalValue);
    } else {
        tempOptionCache[option] = originalValue;
    }

    // Return the value
    return value;
}

/**
 * Delete an option from the settings
 * @param option
 */
export function deleteOption(option: keyof Options): void {
    if(typeof optionCache[option] !== "undefined") {
        delete optionCache[option];
    }

    if(typeof localStorage === "undefined") {
        return;
    }

    localStorage.removeItem(option);
}

/**
 * Delete a temporary option from the session storage
 * @param option
 */
export function deleteTempOption(option: string): void {
    if(typeof tempOptionCache[option] !== "undefined") {
        delete tempOptionCache[option];
    }

    if(typeof sessionStorage === "undefined") {
        return;
    }

    sessionStorage.removeItem(option);
}

/**
 * Retrieve the option keys by providing a prefix.
 * This will return the full key names, not the values
 */
export function getOptionKeysByPrefix(prefix: string): Array<keyof Options> {
    const options = Object.keys(localStorage);
    const keys: Array<keyof Options> = [];

    for(const option of options) {
        if(option.startsWith(prefix)) keys.push(option as keyof Options);
    }

    return keys;
}

/**
 * Retrieve the temporary option keys by providing a prefix.
 * This will return the full key names, not the values
 */
export function getTempOptionKeysByPrefix(prefix: string): Array<string> {
    const options = Object.keys(sessionStorage);
    const keys: Array<string> = [];

    for(const option of options) {
        if(option.startsWith(prefix)) keys.push(option);
    }

    return keys;
}

/**
 * Clear all options from the storage
 */
export function clearOptions(): void {
    const options = { ...localStorage };

    localStorage.clear();

    if(process.env.NODE_ENV === "test") {
        for(const option in optionCache) {
            if(!Object.hasOwn(optionCache, option)) continue;

            delete optionCache[option as keyof typeof optionCache];
        }

        return;
    }

    // Add back any options which start with an underscore
    for(const i in options) {
        if(!Object.prototype.hasOwnProperty.call(options, i)) {
            continue;
        }

        if(i.charAt(0) === "_") {
            setOption(i as keyof Options, options[i]);
        }
    }
}

/**
 * Determine if we have a temporary option already cached
 * @param option
 */
export function hasTempOption(option: string): boolean {
    return typeof tempOptionCache[option] !== "undefined";
}

let temporaryTrainingMode = false;

/**
 * Set whether training mode should be temporarily enabled, you'll also need to disable it within your code
 * @param enabled
 */
export const setTemporaryTrainingMode = (enabled: boolean): void => {
    temporaryTrainingMode = enabled;
};

/**
 * Returns whether training mode is turned on
 * @returns {boolean}
 */
export function isTrainingMode(): boolean {
    if(temporaryTrainingMode) {
        return true;
    }

    return getOption("register_training_sales_mode_enabled") && getOption("training_mode_enabled");
}

/**
 * The OptionSubscriber to keep track of all option subscriptions
 */
export type OptionSubscriberCallback<TKey extends keyof Options> = (
    option: TKey,
    newValue: Options[TKey],
    oldValue: Options[TKey]
) => void;

class OptionSubscriber {
    protected _listeners: Record<string, Array<{
        defaultValue: Options[keyof Options];
        callback: OptionSubscriberCallback<keyof Options>;
    }>>;

    constructor() {
        this._listeners = {};

        this.broadcast = this.broadcast.bind(this);

        this.listenForChanges();
    }

    public subscribe<TKey extends keyof Options>(
        option: TKey,
        callback: OptionSubscriberCallback<TKey>
    ): void {
        if(typeof this._listeners[option] === "undefined") {
            this._listeners[option] = [];
        }

        this._listeners[option].push({
            defaultValue: defaultOptions[option],
            callback    : callback as OptionSubscriberCallback<keyof Options>,
        });
    }

    public unsubscribe<TKey extends keyof Options>(
        option: TKey,
        callback: OptionSubscriberCallback<TKey>
    ): void {
        if(typeof this._listeners[option] === "undefined") {
            return;
        }

        let index = -1;

        for(let i = 0, l = this._listeners[option].length; i < l; i++) {
            if(this._listeners[option][i].callback === callback) {
                index = i;
                break;
            }
        }

        if(index === -1) {
            return;
        }

        this._listeners[option] = [
            ...this._listeners[option].slice(0, index),
            ...this._listeners[option].slice(index + 1),
        ];
    }

    protected listenForChanges(): void {
        if(typeof window !== "undefined") {
            window.addEventListener("storage", this.broadcast);
            // Custom event for changes local to the window
            window.addEventListener("currentStorageUpdate", this.broadcast);
        }
    }

    protected broadcast(e: StorageEvent | CustomEvent): void {
        let key: keyof Options;
        let newValue;
        let oldValue;

        if(e instanceof CustomEvent) {
            key = e.detail.key;
            newValue = e.detail.newValue;
            oldValue = e.detail.oldValue;
        } else {
            key = e.key as keyof Options;
            newValue = e.newValue;
            oldValue = e.oldValue;
        }

        // Determine which listeners match the key
        const matches = this._listeners[key];

        // Get the new value
        if(typeof newValue === "string") {
            newValue = parseString(newValue);
        }

        // Get the old value
        if(typeof oldValue === "string") {
            oldValue = parseString(oldValue);
        }

        // Update the option cache
        optionCache[key] = newValue;

        if(!Array.isArray(matches)) {
            return;
        }

        // Loop through the matches
        for(let i = 0, l = matches.length; i < l; i++) {
            try {
                matches[i].callback(key, newValue, oldValue);
            } catch(err) {
                console.error("A Storage Event callback threw an error:", e, err);
            }
        }
    }

    public clear(): void {
        this._listeners = {};
    }
}

const Subscriber = new OptionSubscriber();

/**
 * Subscribe to an option
 */
export function subscribeToOption<TKey extends keyof Options>(
    option: TKey,
    callback: OptionSubscriberCallback<TKey>
): Options[TKey] {
    Subscriber.subscribe(option, callback);

    return getOption(option);
}

/**
 * Unsubscribe from an option
 */
export function unsubscribeFromOption<TKey extends keyof Options>(
    option: TKey,
    callback: OptionSubscriberCallback<TKey>
): void {
    Subscriber.unsubscribe(option, callback);
}

/**
 * Remove all subscription listeners
 */
export function clearSubscriptions(): void {
    Subscriber.clear();
}

/**
 * Hook to subscribe to the latest value of an option
 * @param option
 */
export function useOption<TKey extends keyof Options>(option: TKey): Options[TKey] {
    const [ value, setValue ] = useState(() => {
        return getOption(option);
    });

    useEffect(() => {
        const listener: OptionSubscriberCallback<TKey> = (_, newValue) => {
            setValue(newValue);
        };

        setValue(subscribeToOption(option, listener));

        return () => {
            unsubscribeFromOption(option, listener);
        };
    }, [ option ]);

    return value;
}
